/**
 * @fileoverview Shared canvas-HTML export — one blob builder for every
 * surface that turns canvas-style HTML (TipTap markup plus the
 * chart/spreadsheet placeholder divs) into a PDF, Word or Excel file.
 *
 * Used by `CanvasPanel` (agent canvas) and `ReportingView` (job reports),
 * which share the same export matrix: format (pdf/docx/xlsx) × destination
 * (direct download / save to the job's documents).
 */

import { canvasToPdfBlob, renderCanvasBlocksForPrint } from "./canvasToPdfBlob";
import { chartSpecFromAttr } from "@/components/agents/universal/canvas/chartHtml";
import { diagramSpecFromAttr } from "@/components/agents/universal/canvas/diagramHtml";
import { spreadsheetSpecFromAttr } from "@/components/agents/universal/canvas/spreadsheetHtml";
import { signatureSpecFromAttr } from "@/components/agents/universal/canvas/signatureHtml";

export type CanvasExportFormat = "pdf" | "docx" | "xlsx";
export type CanvasExportDestination = "download" | "documents";

export const CANVAS_EXPORT_MIME = {
  pdf: "application/pdf",
  docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
} as const satisfies Record<CanvasExportFormat, string>;

export const CANVAS_EXPORT_LABEL: Record<CanvasExportFormat, string> = {
  pdf: "PDF",
  docx: "Word (.docx)",
  xlsx: "Excel (.xlsx)",
};

/** `${title} — ${YYYY-MM-DD}.${ext}`, unsafe filename characters replaced. */
export function canvasExportFileName(
  title: string,
  format: CanvasExportFormat,
): string {
  const safeTitle = title.replace(/[^\w\-. ]/g, "_").slice(0, 80);
  const stamp = new Date().toISOString().slice(0, 10);
  return `${safeTitle} — ${stamp}.${format}`;
}

function escapeHtmlText(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

/**
 * Render canvas HTML to a Blob in the requested format.
 *
 * - `pdf` — image-based A4 capture via `canvasToPdfBlob`.
 * - `docx` — chart/spreadsheet placeholders pre-rendered to static markup
 *   (Word can't run React), then through `buildWordBlob`; a title heading is
 *   injected when the document doesn't already start with one, mirroring the
 *   PDF path.
 * - `xlsx` — the document flow is flattened into a worksheet: text blocks
 *   become single-cell rows, tables and spreadsheet blocks keep their grid.
 */
export async function buildCanvasExportBlob(opts: {
  html: string;
  title: string;
  footer: string;
  format: CanvasExportFormat;
}): Promise<Blob> {
  const { html, title, footer, format } = opts;
  if (format === "pdf") {
    return canvasToPdfBlob({ html, title, footer });
  }
  if (format === "docx") {
    const { buildWordBlob } = await import("@/utils/export/exportHtmlDocument");
    const rendered = await renderCanvasBlocksForPrint(html, "word");
    const heading = /^\s*<h[1-3][\s>]/i.test(rendered)
      ? ""
      : `<h1>${escapeHtmlText(title)}</h1>`;
    return buildWordBlob(
      `${heading}${rendered}<p style="margin-top:24px;color:#888888;font-size:10px;">${escapeHtmlText(footer)}</p>`,
    );
  }
  return buildExcelBlob(html, title);
}

type Cell = string | number;

/**
 * Excel export preserves the document's visible structure rather than dumping
 * raw data: blocks appear in reading order, separated by blank rows. Charts
 * can't live in a worksheet, so they are noted by title.
 */
async function buildExcelBlob(html: string, title: string): Promise<Blob> {
  const [XLSX, { specToAoa }, { AI_DISCLAIMER }] = await Promise.all([
    import("xlsx"),
    import("@/utils/export/specToWorkbook"),
    import("@cvx/lib/aiDisclaimer"),
  ]);

  const rows = canvasHtmlToRows(html, specToAoa);
  const startsWithHeading = /^\s*<h[1-3][\s>]/i.test(html.trim());
  const aoa: Cell[][] = [
    [AI_DISCLAIMER],
    [],
    ...(startsWithHeading ? [] : [[title], []]),
    ...rows,
  ];

  const ws = XLSX.utils.aoa_to_sheet(aoa);
  // Size columns from grid rows only — long single-cell text rows (the
  // disclaimer, paragraphs) would otherwise blow out column A.
  const widths: number[] = [];
  for (const row of aoa) {
    if (row.length < 2) continue;
    row.forEach((cell, i) => {
      widths[i] = Math.max(
        widths[i] ?? 10,
        Math.min(String(cell).length + 2, 60),
      );
    });
  }
  if (widths.length > 0) {
    ws["!cols"] = widths.map((wch) => ({ wch: wch || 10 }));
  }

  const wb = XLSX.utils.book_new();
  const sheetName =
    title
      .replace(/[[\]:*?/\\]/g, " ")
      .trim()
      .slice(0, 31) || "Report";
  XLSX.utils.book_append_sheet(wb, ws, sheetName);
  const out = XLSX.write(wb, { bookType: "xlsx", type: "array" });
  return new Blob([out], { type: CANVAS_EXPORT_MIME.xlsx });
}

/** Flatten canvas HTML into worksheet rows, keeping tables as grids. */
function canvasHtmlToRows(
  html: string,
  specToAoa: (typeof import("@/utils/export/specToWorkbook"))["specToAoa"],
): Cell[][] {
  const doc = new DOMParser().parseFromString(html, "text/html");
  const rows: Cell[][] = [];
  const pushBlank = () => {
    if (rows.length > 0 && rows[rows.length - 1].length > 0) rows.push([]);
  };
  const pushText = (text: string) => {
    const t = text.replace(/\s+/g, " ").trim();
    if (t) rows.push([t]);
  };

  const visit = (el: Element) => {
    const tag = el.tagName;
    if (tag === "TABLE") {
      for (const tr of Array.from(el.querySelectorAll("tr"))) {
        rows.push(
          Array.from(tr.querySelectorAll("th,td")).map((cell) =>
            (cell.textContent ?? "").replace(/\s+/g, " ").trim(),
          ),
        );
      }
      pushBlank();
      return;
    }
    if (tag === "UL" || tag === "OL") {
      Array.from(el.querySelectorAll(":scope > li")).forEach((li, i) => {
        const prefix = tag === "OL" ? `${i + 1}. ` : "• ";
        pushText(`${prefix}${li.textContent ?? ""}`);
      });
      pushBlank();
      return;
    }
    if (el.hasAttribute("data-morgan-spreadsheet")) {
      const spec = spreadsheetSpecFromAttr(
        el.getAttribute("data-morgan-spreadsheet") ?? "",
      );
      if (!spec) {
        pushText("Spreadsheet could not be rendered.");
        return;
      }
      const multi = spec.sheets.length > 1;
      for (const sheet of spec.sheets) {
        if (multi) pushText(sheet.name);
        rows.push(...specToAoa(sheet));
        pushBlank();
      }
      return;
    }
    if (el.hasAttribute("data-morgan-signature")) {
      const spec = signatureSpecFromAttr(
        el.getAttribute("data-morgan-signature") ?? "",
      );
      const signedAs = spec.name ?? spec.text;
      pushText(signedAs ? `Signed: ${signedAs}` : "Signature");
      pushBlank();
      return;
    }
    if (el.hasAttribute("data-morgan-diagram")) {
      const spec = diagramSpecFromAttr(
        el.getAttribute("data-morgan-diagram") ?? "",
      );
      pushText(
        spec
          ? `Diagram: ${spec.title} (see the PDF or Word export for the rendered diagram)`
          : "Diagram could not be rendered.",
      );
      pushBlank();
      return;
    }
    if (el.hasAttribute("data-morgan-chart")) {
      const spec = chartSpecFromAttr(
        el.getAttribute("data-morgan-chart") ?? "",
      );
      pushText(
        spec
          ? `Chart: ${spec.title} (see the PDF or Word export for the rendered chart)`
          : "Chart could not be rendered.",
      );
      pushBlank();
      return;
    }
    if (/^H[1-6]$/.test(tag)) {
      pushBlank();
      pushText(el.textContent ?? "");
      return;
    }
    if (tag === "HR") {
      pushBlank();
      return;
    }
    // Paragraphs, blockquotes and anything else text-like.
    pushText(el.textContent ?? "");
  };

  for (const el of Array.from(doc.body.children)) visit(el);
  // Trim a trailing blank row.
  while (rows.length > 0 && rows[rows.length - 1].length === 0) rows.pop();
  return rows;
}
